import { useState, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { UpkeepMapRaw, UpkeepMapFilters } from '../types';
import { UpkeepMapApiService } from '../services/upkeepMapApiService';
import { UPKEEP_MAP_DEFAULTS } from '../apiConfig';

const defaultFilters = (): UpkeepMapFilters => {
    const now = new Date();
    return {
        company_id: UPKEEP_MAP_DEFAULTS.COMPANY_ID,
        budget_year: now.getFullYear().toString(),
        budget_month: String(now.getMonth() + 1).padStart(2, '0'),
        revision: UPKEEP_MAP_DEFAULTS.REVISION,
    };
};

export function useUpkeepMap() {
    const toast = useToast();
    const [data, setData] = useState<UpkeepMapRaw[]>([]);
    const [referenceData, setReferenceData] = useState<UpkeepMapRaw[]>([]);
    const [loading, setLoading] = useState(false);
    const [filters, setFilters] = useState<UpkeepMapFilters>(defaultFilters());

    const fetchData = useCallback(async (appliedFilters: UpkeepMapFilters) => {
        if (!appliedFilters.activity_id) return;

        const baseParams = {
            budget_year: appliedFilters.budget_year,
            company_id: appliedFilters.company_id,
            revision: appliedFilters.revision,
            estate_id: appliedFilters.estate_id,
            budget_month: appliedFilters.budget_month,
        };

        setLoading(true);
        try {
            const [mainResult, refResult] = await Promise.all([
                UpkeepMapApiService.getBlockReport({ activity_id: appliedFilters.activity_id, ...baseParams }),
                appliedFilters.reference_activity_id
                    ? UpkeepMapApiService.getBlockReport({ activity_id: appliedFilters.reference_activity_id, ...baseParams })
                    : Promise.resolve([]),
            ]);
            setData(mainResult);
            setReferenceData(refResult);
        } catch (err) {
            toast({
                title: 'Gagal memuat data',
                description: String(err),
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setLoading(false);
        }
    }, [toast]);

    const updateFilters = useCallback((newFilters: UpkeepMapFilters) => {
        setFilters(newFilters);
    }, []);

    const clearFilters = useCallback(() => {
        const cleared = defaultFilters();
        setFilters(cleared);
        setData([]);
        setReferenceData([]);
    }, []);

    return { data, referenceData, loading, filters, fetchData, updateFilters, clearFilters };
}
